import { BaseController } from 'controllers/BaseController'
import { OnRequestChangeCallback } from 'services/APIs/BaseAPI'
import {
  ChangeChannelRequest,
  CreateRequestForQuotationRequest,
  CreateRFQFromProjectCommandResponse,
  OutgoingRequestForQuotationDto,
  OutgoingRequestForQuotationState,
  RequestForQuotationDto,
  SendRequestForQuotationResponse,
} from 'services/APIs/InternalAPI/internal-api.contracts'
import { InternalAPI } from 'services/APIs/InternalAPI/InternalAPI'
import { GetContext } from 'store/storeUtils'
import { CreateSupplierRequestsWithEndpointsRequest } from '../quotationExchange.compatibility'

export type OutgoingRfq = OutgoingRequestForQuotationDto
export { OutgoingRequestForQuotationState as OutgoingRfqState }

export const outgoingRfqRequestIds = {
  list: 'ListOutgoingRfqs',
  detail: (rfqId: string) => `GetOutgoingRfq-${rfqId}`,
  send: (rfqId: string) => `SendOutgoingRfq-${rfqId}`,
  retry: (rfqId: string) => `RetryOutgoingRfq-${rfqId}`,
  changeChannel: (rfqId: string) => `ChangeOutgoingRfqChannel-${rfqId}`,
  download: (rfqId: string) => `DownloadOutgoingRfq-${rfqId}`,
  delete: (rfqId: string) => `DeleteOutgoingRfq-${rfqId}`,
} as const

export class OutgoingRfqsClient extends InternalAPI {
  constructor(partyId: string, onRequestChange: OnRequestChangeCallback) {
    super(
      `/api/parties/${partyId}/request-for-quotations`,
      onRequestChange,
    )
  }

  list(): Promise<OutgoingRequestForQuotationDto[]> {
    return this.GetAsync({ relativePath: '/outgoing', id: outgoingRfqRequestIds.list })
  }

  detail(rfqId: string): Promise<OutgoingRequestForQuotationDto> {
    return this.GetAsync({
      relativePath: `/outgoing/${rfqId}`,
      id: outgoingRfqRequestIds.detail(rfqId),
    })
  }

  send(rfqId: string): Promise<SendRequestForQuotationResponse> {
    return this.PostAuthenticatedAsync({
      relativePath: `/outgoing/${rfqId}/send`,
      id: outgoingRfqRequestIds.send(rfqId),
    })
  }

  retry(rfqId: string): Promise<SendRequestForQuotationResponse> {
    return this.PostAuthenticatedAsync({
      relativePath: `/outgoing/${rfqId}/retry`,
      id: outgoingRfqRequestIds.retry(rfqId),
    })
  }

  changeChannel(
    rfqId: string,
    request: ChangeChannelRequest,
  ): Promise<OutgoingRequestForQuotationDto> {
    return this.PutAuthenticatedAsync({
      relativePath: `/outgoing/${rfqId}/channel`,
      data: request,
      id: outgoingRfqRequestIds.changeChannel(rfqId),
    })
  }

  createSupplierRequests(
    rfqId: string,
    request: CreateSupplierRequestsWithEndpointsRequest,
  ): Promise<OutgoingRequestForQuotationDto[]> {
    return this.PostAuthenticatedAsync({
      relativePath: `/${rfqId}/supplier-requests`,
      data: request,
      id: `CreateRfqSupplierRequests-${rfqId}`,
    })
  }

  listCanonical(buyerProjectId?: string): Promise<RequestForQuotationDto[]> {
    return this.GetAsync({
      relativePath: '',
      params: buyerProjectId ? { buyerProjectId } : undefined,
      id: 'ListRequestForQuotations',
    })
  }

  createCanonical(
    request: CreateRequestForQuotationRequest,
  ): Promise<CreateRFQFromProjectCommandResponse> {
    return this.PostAuthenticatedAsync({
      relativePath: '',
      data: request,
      id: 'CreateRequestForQuotation',
    })
  }

  deleteCanonical(rfqId: string): Promise<void> {
    return this.DeleteAuthenticatedAsync({
      relativePath: `/${rfqId}`,
      id: `DeleteRequestForQuotation-${rfqId}`,
    })
  }

  download(rfqId: string) {
    return this.DownloadAsync({
      relativePath: `/outgoing/${rfqId}/document`,
      id: outgoingRfqRequestIds.download(rfqId),
    })
  }

  delete(rfqId: string): Promise<void> {
    return this.DeleteAuthenticatedAsync({
      relativePath: `/outgoing/${rfqId}`,
      id: outgoingRfqRequestIds.delete(rfqId),
    })
  }
}

export class OutgoingRfqsController extends BaseController<OutgoingRfqsClient> {
  constructor() {
    super((onRequestChange) => {
      const { partyId } = GetContext()
      return new OutgoingRfqsClient(partyId, onRequestChange)
    })
  }

  private handle<T>(operation: () => Promise<T>): Promise<T> {
    return operation().catch((error) => Promise.reject(this.HandleError(error)))
  }

  list() {
    return this.handle(() => this.api.list())
  }

  detail(rfqId: string) {
    return this.handle(() => this.api.detail(rfqId))
  }

  send(rfqId: string) {
    return this.handle(async () => {
      const response = await this.api.send(rfqId)
      if (!response.success) {
        throw new Error(response.message || 'Failed to send request for quotation.')
      }
      return response
    })
  }

  retry(rfqId: string) {
    return this.handle(async () => {
      const response = await this.api.retry(rfqId)
      if (!response.success) {
        throw new Error(response.message || 'Failed to retry request for quotation.')
      }
      return response
    })
  }

  changeChannel(rfqId: string, request: ChangeChannelRequest) {
    return this.handle(() => this.api.changeChannel(rfqId, request))
  }

  createSupplierRequests(
    rfqId: string,
    request: CreateSupplierRequestsWithEndpointsRequest,
  ) {
    return this.handle(() => this.api.createSupplierRequests(rfqId, request))
  }

  listCanonical(buyerProjectId?: string) {
    return this.handle(() => this.api.listCanonical(buyerProjectId))
  }

  createCanonical(request: CreateRequestForQuotationRequest) {
    return this.handle(() => this.api.createCanonical(request))
  }

  deleteCanonical(rfqId: string) {
    return this.handle(() => this.api.deleteCanonical(rfqId))
  }

  sendDelivery(deliveryId: string) {
    return this.send(deliveryId)
  }

  async retryDelivery(deliveryId: string) {
    const response = await this.retry(deliveryId)
    if (response.success === false) {
      throw new Error(response.message || 'RFQ retry failed.')
    }
    return response
  }

  downloadDelivery(deliveryId: string) {
    return this.download(deliveryId)
  }

  changeDeliveryChannel(deliveryId: string, request: ChangeChannelRequest) {
    return this.changeChannel(deliveryId, request)
  }

  download(rfqId: string) {
    return this.handle(() => this.api.download(rfqId))
  }

  delete(rfqId: string) {
    return this.handle(() => this.api.delete(rfqId))
  }
}
